import { paths } from "api/schema/api-schema";
import createClient, {
  ClientPathsWithMethod,
  FetchOptions,
  FetchResponse,
} from "openapi-fetch";
import { HttpMethod, PathsWithMethod } from "openapi-typescript-helpers";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { DjangoClientPath } from "../types/DjangoClient.types";
import { getEnvVar } from "ideas.env";
import {
  useQueryClient,
  useQuery as _useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import {
  DjangoClientInvalidateQueryFilters,
  DjangoClientQueryFnData,
} from "../types/DjangoClient.types";
import { isEqual } from "lodash";
import { captureException } from "@sentry/react";

/**
 * Raw API Client
 */
const client = createClient<paths>({
  baseUrl: getEnvVar("URL_DJANGO_v0"),
});

/**
 * Middleware to attach headers
 */
client.use({
  onRequest: async ({ request }) => {
    const tenantIdHeaderKey = "x-tenant-id";
    const authHeaderKey = "authorization";
    const headers = await getRequestHeaders();

    request.headers.set(authHeaderKey, headers[authHeaderKey]);
    if (tenantIdHeaderKey in headers) {
      request.headers.set(
        tenantIdHeaderKey,
        String(headers[tenantIdHeaderKey]),
      );
    } else {
      request.headers.delete(tenantIdHeaderKey);
    }
  },
  onError: ({ error }) => {
    captureException(error);
  },
});

const getQueryKey = <Params extends (string | number)[]>(
  path: DjangoClientPath,
  ...params: Params
) => [path, ...params];

const useInvalidateQuery = <
  Path extends DjangoClientPath,
  Params extends (string | number)[],
>(
  path: Path,
  ...params: Params
) => {
  const queryKey = getQueryKey(path, ...params);
  const [queryKeyMemo, setQueryKeyMemo] = useState(queryKey);

  /**
   * Memoize query key
   * React Query is smart enough to compare query keys
   * element by element, but an invalidate function from a hook
   * that depends on a query key array is not, so this memoizes
   * that array reference
   *
   */
  useEffect(() => {
    setQueryKeyMemo((prev) => {
      if (!isEqual(prev, queryKey)) {
        return queryKey;
      }
      return prev;
    });
  }, [queryKey]);

  const queryClient = useQueryClient();

  const invalidateQuery = useCallback(
    (filters: DjangoClientInvalidateQueryFilters) => {
      return queryClient.invalidateQueries({
        queryKey: queryKeyMemo,
        ...filters,
      });
    },
    [queryClient, queryKeyMemo],
  );

  return { invalidateQuery };
};

/**
 * React Query options
 * queryKey and queryFn are owned by the factory
 */
type DjangoClientQueryOptions<R> = Omit<
  UseQueryOptions<R>,
  "queryKey" | "queryFn"
>;

const djangoClientReactQueryFactory = <
  Path extends PathsWithMethod<paths, "get">,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Fn extends (...args: any[]) => Promise<any>,
  FnP extends Parameters<Fn>,
>(
  path: Path,
  queryFunction: Fn,
  formatQueryKey: (...params: FnP) => (string | number)[],
) => {
  const useQueryWithOptions = (
    options: DjangoClientQueryOptions<DjangoClientQueryFnData<Fn>> | undefined,
    ...params: FnP
  ) => {
    const formattedKey = formatQueryKey(...params);
    const queryKey = getQueryKey(path, "get", ...formattedKey);
    const { invalidateQuery } = useInvalidateQuery(
      path,
      "get",
      ...formattedKey,
    );

    const res = _useQuery<DjangoClientQueryFnData<Fn>>({
      ...options,
      queryKey,
      queryFn: () => queryFunction(...params),
    });
    return { ...res, invalidateQuery };
  };

  const useQuery = (...params: FnP) =>
    useQueryWithOptions(undefined, ...params);

  return { useQuery, useQueryWithOptions };
};

type Endpoint<
  M extends HttpMethod,
  P extends ClientPathsWithMethod<typeof client, M>,
> = {
  method: M;
  path: P;
};

type Input<
  P extends DjangoClientPath,
  M extends HttpMethod,
> = M extends keyof paths[P] ? paths[P][M] : never;

type Operation<
  P extends keyof paths,
  M extends HttpMethod,
> = M extends keyof paths[P] ? NonNullable<paths[P][M]> : never;

type FactoryOptions<
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormatInput extends (...input: any) => FetchOptions<Input<P, M>>,
  R,
> = {
  formatInput: FormatInput;
  handleResponse: (
    res: FetchResponse<
      Operation<P, M>,
      FetchOptions<Input<P, M>>,
      "application/json"
    >,
  ) => R;
} & (M extends "get"
  ? {
      formatQueryKey: (
        ...params: Parameters<FormatInput>
      ) => (string | number)[];
    }
  : { formatQueryKey?: never });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DjangoClientUseQuery<P extends any[], R> = (
  ...args: P
) => UseQueryResult<R> & {
  invalidateQuery: (
    filters: DjangoClientInvalidateQueryFilters,
  ) => Promise<void>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DjangoClientUseQueryWithOptions<P extends any[], R> = (
  options: DjangoClientQueryOptions<R> | undefined,
  ...params: P
) => UseQueryResult<R> & {
  invalidateQuery: (
    filters: DjangoClientInvalidateQueryFilters,
  ) => Promise<void>;
};

type QueryFactoryResult<
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormatInput extends (...input: any) => FetchOptions<Input<P, M>>,
  R,
> = M extends "get"
  ? {
      queryFn: (...input: Parameters<FormatInput>) => Promise<R>;
      useQuery: DjangoClientUseQuery<Parameters<FormatInput>, R>;
      useQueryWithOptions: DjangoClientUseQueryWithOptions<
        Parameters<FormatInput>,
        R
      >;
    }
  : {
      queryFn: (...input: Parameters<FormatInput>) => Promise<R>;
    };

export const djangoClientQueryFactory = <
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
  R,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormatInput extends (...input: any[]) => FetchOptions<Input<P, M>>,
>(
  endpoint: Endpoint<M, P>,
  {
    formatInput,
    handleResponse,
    formatQueryKey,
  }: FactoryOptions<M, P, FormatInput, R>,
): QueryFactoryResult<M, P, FormatInput, R> => {
  const queryFn = async (...input: Parameters<FormatInput>) => {
    const res = await client.request(
      endpoint.method,
      endpoint.path,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatInput(...input) as any,
    );
    // union too complex to represent, expected from large set of possible endpoints
    // eslint-disable-next-line
    // @ts-expect-error
    return handleResponse(res);
  };

  if (endpoint.method === "get") {
    const { useQuery, useQueryWithOptions } = djangoClientReactQueryFactory(
      endpoint.path as PathsWithMethod<paths, "get">,

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore:  TS 4X problem... not letting my assign a more narrow type to a function that accepts any
      queryFn,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      formatQueryKey as any,
    );

    return {
      queryFn,
      useQuery: useQuery satisfies DjangoClientUseQuery<
        Parameters<FormatInput>,
        R
      >,
      useQueryWithOptions:
        useQueryWithOptions satisfies DjangoClientUseQueryWithOptions<
          Parameters<FormatInput>,
          R
        >,
    } as QueryFactoryResult<M, P, FormatInput, R>;
  }

  return { queryFn } as QueryFactoryResult<M, P, FormatInput, R>;
};
